import React, { useState, useEffect, useCallback } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Form, { FormSection } from '@atlaskit/form';
import { useFlagsService } from '@atlassian/jira-flags';
import { fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { ProjectKey } from '@atlassian/jira-shared-types/src/general.tsx';
import {
	type PortalAccessEnum,
	type ShareRequestAccessEnum,
	PORTAL_ACCESS,
	SHARE_REQUESTS_ACCESS,
} from '../../common/types';
import type { RequestSecurityRequestBody, SecuritySettings } from '../../services/types';
import { FormContent } from './content';
import { Footer as FormFooter } from './footer';
import messages from './messages';
import { useUpdateEffect } from './util';

export type Props = {
	isProjectSimplified: boolean;
	projectKey: ProjectKey;
	settings: SecuritySettings;
	loading: boolean;
	error: string | undefined;
	onSaveRequestSecurity: (
		arg1: RequestSecurityRequestBody,
		arg2: UIAnalyticsEvent,
	) => Promise<void>;
};

export const CustomerPermissionsForm = ({
	loading,
	error,
	settings,
	projectKey,
	isProjectSimplified,
	onSaveRequestSecurity,
}: Props) => {
	const [portalAccess, setPortalAccess] = useState<PortalAccessEnum>(settings.portalAccess);
	const [shareRequestsAccess, setRequestAccess] = useState<ShareRequestAccessEnum>(
		settings.shareRequestsAccess,
	);

	const [hasFormChanged, setFormChanged] = useState(false);
	const resetState = useCallback(() => {
		setPortalAccess(settings.portalAccess);
		setRequestAccess(settings.shareRequestsAccess);
	}, [settings.portalAccess, settings.shareRequestsAccess]);

	const onSave = useCallback(
		// @ts-expect-error - TS7006 - Parameter '_' implicitly has an 'any' type. | TS7006 - Parameter 'analyticsEvent' implicitly has an 'any' type.
		async (_, analyticsEvent) => {
			const saveRequestData = {
				autocompleteEnabled: shareRequestsAccess === SHARE_REQUESTS_ACCESS.SEARCH_AUTO_COMPLETE,
				manageEnabled: shareRequestsAccess !== SHARE_REQUESTS_ACCESS.AGENT_ONLY,
				serviceDeskOpenAccess: portalAccess !== PORTAL_ACCESS.RESTRICTED,
				serviceDeskPublicSignup: portalAccess !== PORTAL_ACCESS.RESTRICTED,
			};

			onSaveRequestSecurity(saveRequestData, analyticsEvent);
			fireTrackAnalytics(
				{ ...analyticsEvent, payload: null },
				'customerPermissionsSettings updated',
			);
		},
		[shareRequestsAccess, portalAccess, onSaveRequestSecurity],
	);

	useEffect(() => {
		setFormChanged(
			portalAccess !== settings.portalAccess ||
				shareRequestsAccess !== settings.shareRequestsAccess,
		);
	}, [portalAccess, settings.portalAccess, shareRequestsAccess, settings.shareRequestsAccess]);

	const { showFlag } = useFlagsService();

	useUpdateEffect(() => {
		if (loading) return;

		if (typeof error === 'string') {
			showFlag({
				type: 'error',
				title: messages.failureMessage,
				description: messages.failureDescriptionMessage,
				isAutoDismiss: false,
			});
			return;
		}

		showFlag({
			type: 'success',
			title: messages.successMessage,
			isAutoDismiss: true,
		});
	}, [error, loading, showFlag]);

	return (
		// Replace with lodash/noop
		// eslint-disable-next-line @typescript-eslint/no-empty-function
		<Form onSubmit={() => {}}>
			{({ formProps }) => (
				<form {...formProps} name="customer-permissions-form">
					<FormSection>
						<FormContent
							projectKey={projectKey}
							settings={settings}
							portalAccess={portalAccess}
							isProjectSimplified={isProjectSimplified}
							onPortalAccessChange={setPortalAccess}
							shareRequestsAccess={shareRequestsAccess}
							onShareRequestAccessChange={setRequestAccess}
						/>
					</FormSection>
					<FormFooter
						isLoading={loading}
						hasFormChanged={hasFormChanged}
						onSubmit={onSave}
						onCancel={resetState}
					/>
				</form>
			)}
		</Form>
	);
};
